import React, { useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { PLANET_NATURE } from './HoraSection';

/*
 * HoraDrillDown — modal showing all 24 horas as a vertical table.
 *
 * Inline implementation following ShareImagePreview pattern (no shared modal
 * primitive in this issue — tracked as a follow-up unification issue).
 *
 *  - role="dialog", aria-modal="true"
 *  - Focus trap inside while open; ESC closes
 *  - Focus returns to originating link on close
 */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${p => p.theme.colors.modal?.backdrop || 'rgba(0,0,0,0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${p => p.theme.spacing[4]}px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    padding: 0;
  }
`;

const ModalBox = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: ${p => p.theme.radius.md}px;
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${p => p.theme.shadow.modal || p.theme.shadow.card};

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${p => p.theme.spacing[3]}px ${p => p.theme.spacing[4]}px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: ${p => p.theme.typography.weights.semibold};
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: ${p => p.theme.colors.text.secondary};
  padding: 4px 8px;
  min-width: 44px;
  min-height: 44px;

  &:hover { color: ${p => p.theme.colors.text.primary}; }
  &:focus {
    outline: 2px solid ${p => p.theme.colors.bands.exceptional};
    outline-offset: 2px;
  }
`;

const HoraTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }

  thead th {
    background: ${p => p.theme.colors.background};
    font-weight: ${p => p.theme.typography.weights.semibold};
    color: ${p => p.theme.colors.text.secondary};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  tbody tr.auspicious td:first-child::before {
    content: '⊕ ';
    color: ${p => p.theme.colors.tone?.green?.text || '#27AE60'};
  }

  tbody tr.inauspicious td:first-child::before {
    content: '⊖ ';
    color: ${p => p.theme.colors.tone?.red?.text || '#E74C3C'};
  }

  tbody td.mono {
    font-family: ${p => p.theme.typography.monoFamily};
    color: ${p => p.theme.colors.text.secondary};
  }
`;

function getFocusable(el) {
  if (!el) return [];
  return Array.from(el.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  ));
}

const HoraDrillDown = ({ open, onClose, horas = [], lang = 'en' }) => {
  const theme = useTheme();
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = getFocusable(modalRef.current);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    setTimeout(() => {
      const focusable = getFocusable(modalRef.current);
      if (focusable.length > 0) focusable[0].focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Backdrop onClick={onClose}>
      <ModalBox
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={lang === 'ta' ? '24 ஹோரைகள்' : 'All 24 Horas'}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            {lang === 'ta' ? '24 ஹோரைகள்' : 'All 24 Horas'}
          </ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label={lang === 'ta' ? 'மூடு' : 'Close'}>
            ×
          </CloseButton>
        </ModalHeader>
        <HoraTable>
          <thead>
            <tr>
              <th>{lang === 'ta' ? '#' : '#'}</th>
              <th>{lang === 'ta' ? 'கிரகம்' : 'Planet'}</th>
              <th>{lang === 'ta' ? 'தொடக்கம்' : 'Start'}</th>
              <th>{lang === 'ta' ? 'முடிவு' : 'End'}</th>
            </tr>
          </thead>
          <tbody>
            {horas.map((h, i) => {
              const tone = h.planet ? PLANET_NATURE[h.planet] : undefined;
              const toneColor = tone && tone !== 'neutral'
                ? theme.colors.accentTones[tone]
                : undefined;
              return (
                <tr key={i} className={h.nature || ''}>
                  <td>{i + 1}</td>
                  <td style={toneColor ? { color: toneColor } : undefined}>{h.planet || '—'}</td>
                  <td className="mono">{h.start ? h.start.substring(0, 5) : '—'}</td>
                  <td className="mono">{h.end ? h.end.substring(0, 5) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </HoraTable>
      </ModalBox>
    </Backdrop>
  );
};

export default HoraDrillDown;
